import React from "react";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { getComplianceFrameworkBadgeText, getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";
import { AH_BORDER, AH_MUTED, AH_TEXT, AH_TRACK, AH_WHITE, cardShellSx, tableShellSx } from "../AuditHub/theme";

/** Building blocks shared by the audit cards on the Controls page, on the Audit Hub tokens. */

export const PASSED_COLOR = "#a5d6a7";
export const FAILED_COLOR = "#ef9a9a";
export const PASSED_TEXT_COLOR = "#16a34a";
export const FAILED_TEXT_COLOR = "#dc2626";
export const EMPTY_TRACK = AH_BORDER;

export const labelSx = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: AH_MUTED,
  lineHeight: 1.3,
};

export const bigNumberSx = { fontSize: "1.6rem", fontWeight: 700, color: AH_TEXT, lineHeight: 1 };

export const formatCount = (value) => (Number(value) || 0).toLocaleString();
export const percentOf = (part, total) => (total > 0 ? (part / total) * 100 : 0);

export const CardShell = ({ children, sx }) => (
  <Box sx={{ ...tableShellSx, p: { xs: 1.5, sm: 2 }, display: "flex", flexDirection: "column", gap: 1.5, ...sx }}>{children}</Box>
);

export const CardHeader = ({ eyebrow, compliance, meta, action }) => {
  const iconSrc = getComplianceIcon(compliance);
  const badgeText = getComplianceFrameworkBadgeText(compliance);
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, rowGap: 1.25, flexWrap: "wrap" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: "1 1 320px" }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: AH_WHITE,
            border: `1px solid ${AH_BORDER}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {iconSrc ? (
            <img src={iconSrc} alt="" style={{ maxWidth: 26, maxHeight: 26 }} />
          ) : (
            <Typography sx={{ fontWeight: 700, fontSize: 12, color: AH_TEXT }}>{badgeText || "—"}</Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={labelSx}>{eyebrow}</Typography>
          <Typography title={compliance} sx={{ fontWeight: 700, fontSize: "1rem", color: AH_TEXT, lineHeight: 1.3, overflowWrap: "anywhere" }}>
            {compliance}
          </Typography>
          {meta ? <Box sx={{ mt: 0.25, fontSize: 13, color: AH_MUTED }}>{meta}</Box> : null}
        </Box>
      </Box>
      {action ? (
        <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, flex: { xs: "1 1 100%", sm: "0 1 380px" }, minWidth: 0 }}>
          {action}
        </Box>
      ) : null}
    </Box>
  );
};

export const Tile = ({ label, action, children, sx }) => (
  <Box sx={{ ...cardShellSx, px: 1.5, py: 1.25, display: "flex", flexDirection: "column", gap: 1, minWidth: 0, ...sx }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 22 }}>
      <Typography noWrap sx={{ ...labelSx, minWidth: 0 }}>
        {label}
      </Typography>
      {action}
    </Box>
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

/** Three equal tiles on wide screens; the last tile spans the row on tablets; stacked on phones. */
export const tileGridSx = {
  display: "grid",
  gap: 1.25,
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.3fr)",
  },
};
export const wideTileSx = { gridColumn: { sm: "1 / -1", lg: "auto" } };

const RING_SIZE = 72;

export const ScoreRing = ({ value, tooltip }) => {
  const hasValue = Number.isFinite(value);
  const clamped = hasValue ? Math.min(100, Math.max(0, value)) : 0;
  return (
    <Tooltip title={tooltip || ""} arrow placement="top">
      <Box sx={{ position: "relative", width: RING_SIZE, height: RING_SIZE, flexShrink: 0 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={RING_SIZE}
          thickness={3.5}
          sx={{ color: hasValue ? FAILED_COLOR : EMPTY_TRACK, position: "absolute", inset: 0 }}
        />
        <CircularProgress
          variant="determinate"
          value={clamped}
          size={RING_SIZE}
          thickness={3.5}
          sx={{ color: PASSED_COLOR, position: "absolute", inset: 0 }}
        />
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: AH_TEXT, lineHeight: 1 }}>
            {hasValue ? `${clamped.toFixed(2)}%` : "—"}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export const SplitBar = ({ passed, total, tooltip, height = 8 }) => (
  <Tooltip title={tooltip || ""} arrow placement="top">
    <Box sx={{ height, borderRadius: 99, overflow: "hidden", display: "flex", bgcolor: total > 0 ? FAILED_COLOR : AH_TRACK }}>
      <Box sx={{ width: `${percentOf(passed, total)}%`, bgcolor: PASSED_COLOR, transition: "width 0.4s ease" }} />
    </Box>
  </Tooltip>
);

const CountRow = ({ color, label, value, divider }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 1,
      minWidth: 0,
      py: 0.75,
      ...(divider ? { borderTop: `1px solid ${AH_BORDER}` } : null),
    }}
  >
    <Typography sx={{ fontSize: 13, color: AH_MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</Typography>
    <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color, lineHeight: 1.2 }}>{value}</Typography>
  </Box>
);

/** "111 passed" style figure: the number carries the colour, the word stays muted. */
const InlineCount = ({ color, value, label, align = "left" }) => (
  <Typography sx={{ fontSize: 13, color: AH_MUTED, textAlign: align, whiteSpace: "nowrap" }}>
    <Box component="span" sx={{ fontWeight: 700, color, mr: 0.5 }}>
      {value}
    </Box>
    {label}
  </Typography>
);

/** Ring with the passed / failed control counts beside it. */
export const ScoreTileBody = ({ score, passed, failed, tooltip, missing }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
    <ScoreRing value={score} tooltip={tooltip} />
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <CountRow color={PASSED_TEXT_COLOR} label="Passed Controls" value={missing ? "—" : formatCount(passed)} />
      <CountRow color={FAILED_TEXT_COLOR} label="Failed Controls" value={missing ? "—" : formatCount(failed)} divider />
    </Box>
  </Box>
);

/** Total, pass rate, split bar and passed / failed counts for scanned resources. */
export const ResourcesTileBody = ({ total, passed, failed, loading, missing }) => {
  const blank = Boolean(loading || missing);
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <Typography sx={bigNumberSx}>{blank ? "—" : formatCount(total)}</Typography>
        <Typography sx={{ fontSize: 13, color: AH_MUTED }}>Resources</Typography>
        {!blank && total > 0 ? (
          <Typography sx={{ ml: "auto", fontSize: 13, fontWeight: 700, color: PASSED_TEXT_COLOR }}>
            {percentOf(passed, total).toFixed(1)}% Pass
          </Typography>
        ) : null}
      </Box>
      <SplitBar
        passed={blank ? 0 : passed}
        total={blank ? 0 : total}
        tooltip={blank ? "" : `Passed: ${formatCount(passed)} | Failed: ${formatCount(failed)}`}
      />
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <InlineCount color={PASSED_TEXT_COLOR} value={blank ? "—" : formatCount(passed)} label="passed" />
        <InlineCount color={FAILED_TEXT_COLOR} value={blank ? "—" : formatCount(failed)} label="failed" align="right" />
      </Box>
    </>
  );
};
